(() => {
  'use strict';

  if (window.__invziKickstarterMotion) return;
  window.__invziKickstarterMotion = true;

  const playedStats = new WeakSet();
  const revealed = new WeakSet();
  const finalText = new WeakMap();
  const integerFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const motionPreference = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  let current = null;

  const canAnimate = () => motionPreference && !motionPreference.matches
    && !(window.Shopify && window.Shopify.designMode)
    && typeof window.IntersectionObserver === 'function'
    && typeof window.requestAnimationFrame === 'function'
    && typeof window.cancelAnimationFrame === 'function';

  function finishReveal(state, element) {
    const active = state.activeReveals.get(element);
    if (active) {
      window.clearTimeout(active.timeout);
      element.removeEventListener('animationend', active.onEnd);
      state.activeReveals.delete(element);
    }
    element.classList.remove('is-revealing');
  }

  function show(state, element) {
    finishReveal(state, element);
    element.classList.add('is-visible');
    revealed.add(element);
    if (state.observer) state.observer.unobserve(element);
  }

  function reveal(state, element) {
    if (revealed.has(element)) { show(state, element); return; }
    revealed.add(element);
    state.observer.unobserve(element);
    const onEnd = (event) => {
      if (event.target === element) finishReveal(state, element);
    };
    // Only clean up a started animation; offscreen content stays fully visible.
    const timeout = window.setTimeout(() => finishReveal(state, element), 650);
    state.activeReveals.set(element, { timeout, onEnd });
    element.addEventListener('animationend', onEnd);
    element.classList.add('is-revealing');
  }

  function restore(state) {
    state.stopped = true;
    if (state.observer) state.observer.disconnect();
    if (state.frame !== null) window.cancelAnimationFrame(state.frame);
    // Keep the server-rendered final text, including its exact formatting.
    state.counters.forEach(({ element }) => { element.textContent = finalText.get(element); });
    state.reveals.forEach((element) => show(state, element));
  }

  function countUp(state) {
    if (playedStats.has(state.overview)) return;
    playedStats.add(state.overview);
    let started = null;

    const step = (time) => {
      if (state.stopped || current !== state) return;
      try {
        if (!canAnimate()) { restore(state); return; }
        if (started === null) started = time;
        const progress = Math.min((time - started) / 900, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        state.counters.forEach(({ element, target, format }) => {
          const value = target * eased;
          element.textContent = format === 'million'
            ? value.toFixed(2)
            : integerFormat.format(Math.round(value));
        });
        if (progress < 1) {
          state.frame = window.requestAnimationFrame(step);
        } else {
          state.frame = null;
          state.counters.forEach(({ element }) => { element.textContent = finalText.get(element); });
        }
      } catch (_) {
        restore(state);
      }
    };
    state.frame = window.requestAnimationFrame(step);
  }

  function mount() {
    if (current) restore(current);
    const root = document.getElementById('invzi-kickstarter-page');
    current = null;
    if (!root) return;

    const state = {
      root,
      overview: root.querySelector('.ks-overview'),
      counters: [],
      reveals: Array.from(root.querySelectorAll('[data-ks-reveal]')),
      activeReveals: new Map(),
      observer: null,
      frame: null,
      stopped: false
    };
    current = state;

    try {
      if (state.overview) {
        state.overview.querySelectorAll('[data-ks-count]').forEach((element) => {
          const target = Number(element.dataset.ksCount);
          const format = element.dataset.ksCountFormat;
          if (!Number.isFinite(target) || target < 0 || !['million', 'integer'].includes(format)) return;
          if (!finalText.has(element)) finalText.set(element, element.textContent);
          state.counters.push({ element, target, format });
        });
      }
      if (!canAnimate()) { restore(state); return; }

      state.observer = new window.IntersectionObserver((entries) => {
        if (state.stopped) return;
        try {
          if (!canAnimate()) { restore(state); return; }
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (entry.target === state.overview) {
              state.observer.unobserve(entry.target);
              countUp(state);
            } else {
              reveal(state, entry.target);
            }
          });
        } catch (_) {
          restore(state);
        }
      }, { threshold: 0.1, rootMargin: '0px 0px -24px 0px' });

      if (state.overview && state.counters.length && !playedStats.has(state.overview)) {
        state.observer.observe(state.overview);
      }
      state.reveals.forEach((element) => {
        // Leave initially visible or already visited content visible.
        if (revealed.has(element) || element.getBoundingClientRect().top < window.innerHeight) {
          show(state, element);
          return;
        }
        state.observer.observe(element);
      });
    } catch (_) {
      restore(state);
    }
  }

  function isRelated(target) {
    if (!current || !target || typeof target.contains !== 'function') return false;
    return current.root.contains(target) || target.contains(current.root)
      || current.reveals.some((element) => target.contains(element))
      || current.counters.some(({ element }) => target.contains(element));
  }

  // Theme editor replacements must leave detached and newly loaded sections safe.
  document.addEventListener('shopify:section:load', (event) => {
    if (isRelated(event.target) || (!current && document.getElementById('invzi-kickstarter-page'))) mount();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (isRelated(event.target)) { restore(current); current = null; }
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (!current) return;
    const blockId = event.detail && event.detail.blockId;
    const element = event.target instanceof Element ? event.target.closest('[data-ks-reveal]') : null;
    const project = element || (blockId && document.getElementById(`ks-project-${blockId}`));
    if (project && current.root.contains(project)) show(current, project);
  });

  if (motionPreference) {
    const onMotionChange = (event) => { if (event.matches && current) restore(current); };
    if (motionPreference.addEventListener) motionPreference.addEventListener('change', onMotionChange);
    else if (motionPreference.addListener) motionPreference.addListener(onMotionChange);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
